import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Ruler, Building2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

export const RealisationDetailPage = () => {
  const navigate = useNavigate();

  const images = [
    "https://customer-assets.emergentagent.com/job_982f1f8b-d55b-4c68-9981-e0def6f33939/artifacts/djjposso_Screenshot%202026-03-22%20at%2002.38.55.png",
    "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/io3x3d9y_ChatGPT%20Image%205%20nov.%202025%2C%2002_47_40.png",
    "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/urn1votf_ChatGPT%20Image%205%20nov.%202025%2C%2002_47_48.png",
    "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/djk5b6u3_ChatGPT%20Image%205%20nov.%202025%2C%2002_50_49.png",
    "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/jb1k07j4_ChatGPT%20Image%205%20nov.%202025%2C%2002_55_43.png"
  ];

  const caracteristiques = [
    { label: "Surface habitable", value: "600 m²" },
    { label: "Surface sous-sol", value: "400 m²" },
    { label: "Niveaux", value: "5 niveaux" },
    { label: "Logements", value: "9 unités" },
    { label: "Commerces", value: "2 surfaces" },
    { label: "Énergie", value: "Installation photovoltaïque" }
  ];

  const programme = [
    "3 appartements de 3.5 pièces",
    "3 appartements de 2.5 pièces",
    "3 studios de 1.5 pièce",
    "2 surfaces de bureaux ou commerces en rez-de-chaussée"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e5a7a] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-[#f5b800] mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Résidence ELEVEN
          </h1>
          <div className="flex flex-wrap gap-4 text-lg">
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              Épalinges, Route de Berne 223
            </div>
            <div className="flex items-center">
              <Calendar className="mr-2" size={20} />
              Avril 2023 - Octobre 2024
            </div>
            <div className="flex items-center">
              <Building2 className="mr-2" size={20} />
              Immeuble en PPE
            </div>
          </div>
        </div>
      </div>

      {/* Carrousel d'images */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Carousel className="w-full">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative h-[500px] md:h-[600px] overflow-hidden rounded-2xl">
                    <img
                      src={image}
                      alt={`Résidence ELEVEN - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
        </div>
      </section>

      {/* Description */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-8">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Notre Expertise
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Conception, coordination et direction complète des travaux pour la construction d'un immeuble résidentiel et commercial situé à Épalinges, sur la Route de Berne 223.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Ce projet illustre parfaitement la maîtrise du cycle immobilier complet, depuis les premières études jusqu'à la livraison clé en main.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Programme Immobilier
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    L'immeuble comprend un total de <strong>9 logements</strong> et <strong>2 surfaces commerciales</strong>, répartis selon les typologies suivantes :
                  </p>
                  <ul className="space-y-3">
                    {programme.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-lg">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-700 mt-6 leading-relaxed">
                    Chaque logement a été conçu pour offrir un confort optimal, une efficacité énergétique élevée et une architecture contemporaine parfaitement intégrée à l'environnement d'Épalinges.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-[#f5b800]">
                    Données Principales
                  </h3>
                  <div className="space-y-4">
                    {caracteristiques.map((item, index) => (
                      <div key={index} className="border-b border-white/20 pb-3">
                        <p className="text-sm text-white/70 mb-1">{item.label}</p>
                        <p className="text-lg font-semibold">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl border-l-4 border-l-[#f5b800]">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-[#1e5a7a] mb-4">
                    Points Clés
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle size={20} className="text-[#f5b800] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Installation photovoltaïque</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={20} className="text-[#f5b800] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Architecture contemporaine</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={20} className="text-[#f5b800] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Efficacité énergétique élevée</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={20} className="text-[#f5b800] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Emplacement privilégié</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Intéressé par nos réalisations ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Découvrez comment nous pouvons concrétiser votre projet immobilier.
          </p>
          <Button
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-[#f5b800] hover:bg-[#d9a400] text-black font-semibold px-8 py-6 text-lg"
          >
            Contactez-nous
          </Button>
        </div>
      </section>
    </div>
  );
};